import { useRef, useState } from 'react';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleOutlined,
  CloudOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  HistoryOutlined,
  ReloadOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { History } from 'lucide-react';

import { DataTable, PageHeader, Tile } from '../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { HopBaoCaoInstance } from '../components/lich-su/HopBaoCaoInstance';
import { HopLogRobot } from '../components/lich-su/HopLogRobot';
import { BadgeTrangThaiLuot } from '../components/lich-su/nhan';
import { QUYEN, TRANG_THAI_INSTANCE } from '../constants';
import { useLichSu } from '../hooks/useLichSu';
import { KHOA_RPA } from '../hooks/useRpa';
import { CO_TRANG_LICH_SU, daXong, duongDuLieu, TRANG_THAI_INSTANCE as DS_TRANG_THAI } from '../nghiep-vu/lich-su';
import type { DongLichSu, TruyVanLichSu } from '../types';

/** Chờ gõ xong rồi mới hỏi máy chủ (tìm trên toàn bộ lịch sử đã lưu, không chỉ trang đang xem). */
const CHO_GO_MS = 300;

/**
 * Lịch sử luồng chạy (`/rpa/history`, `RpaHistoryView` của native): lượt chạy OpenRPA lưu tại Native, đồng bộ từ
 * `openrpa_instances` của OpenCore — gồm cả lượt bấm chạy thẳng trong OpenRPA và lượt OpenCore đã dọn. Báo cáo lỗi và
 * log Robot gửi về gắn theo InstanceId. Phân trang, tìm và lọc trạng thái ở máy chủ.
 */
export function LichSuView() {
  const { t, locale } = useT();
  const qc = useQueryClient();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);
  const [tim, setTim] = useState('');
  const [truyVan, setTruyVan] = useState<TruyVanLichSu>({ page: 1, pageSize: CO_TRANG_LICH_SU[0], search: '', status: '' });
  const henGo = useRef<number | undefined>(undefined);
  const ls = useLichSu(truyVan, quyen.lichSuDoc);
  const dangTai = useIsFetching({ queryKey: KHOA_RPA.lichSu }) > 0;
  const [xemLoi, setXemLoi] = useState<DongLichSu | null>(null);
  const [xemLog, setXemLog] = useState<DongLichSu | null>(null);

  const tieuDe = t('nav.muc.lichSuLuongChay');
  const meta = ls.data?.meta;
  const dong = ls.data?.data ?? [];
  const dangLoc = truyVan.search !== '' || truyVan.status !== '';

  const doiTim = (v: string) => {
    setTim(v);
    window.clearTimeout(henGo.current);
    henGo.current = window.setTimeout(
      () => setTruyVan((q) => (q.search === v.trim() ? q : { ...q, page: 1, search: v.trim() })),
      CHO_GO_MS,
    );
  };

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: t('nav.muc.quanLyProjectWorkflow') }, { label: tieuDe }]}
        titleIcon={<History size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaLichSu.moTa')}
        actions={
          quyen.lichSuDoc ? (
            <Button
              variant="outline"
              size="md"
              icon={<ReloadOutlined />}
              loading={dangTai}
              onClick={() => void qc.invalidateQueries({ queryKey: KHOA_RPA.lichSu })}
            >
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />

      {!quyen.lichSuDoc ? (
        <KhongCoQuyen quyen={QUYEN.lichSuDoc} />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Input
                allowClear
                prefix={<SearchOutlined aria-hidden />}
                placeholder={t('rpaLichSu.tim')}
                aria-label={t('rpaLichSu.tim')}
                value={tim}
                onChange={(e) => doiTim(e.target.value)}
                className="w-full sm:w-80"
              />
              <Select<string>
                value={truyVan.status}
                onChange={(status) => setTruyVan((q) => ({ ...q, page: 1, status }))}
                aria-label={t('rpaLichSu.locTrangThai')}
                className="w-44"
                options={[
                  { value: '', label: t('rpaLichSu.moiTrangThai') },
                  ...DS_TRANG_THAI.map((s) => ({ value: s, label: t(TRANG_THAI_INSTANCE[s]!.khoa) })),
                ]}
              />
            </div>
            {meta && (
              <span
                role="status"
                className={`inline-flex min-w-0 items-center gap-1.5 text-xs ${meta.syncError ? 'text-warning-700' : 'text-content-muted'}`}
              >
                {meta.syncError ? <WarningOutlined aria-hidden /> : <CloudOutlined aria-hidden />}
                {meta.syncError
                  ? t('rpaLichSu.nguonLoi', { tong: soVN(meta.total, locale) })
                  : t('rpaLichSu.nguon', {
                      tong: soVN(meta.total, locale),
                      luc: meta.lastSyncAt ? ngayGioVN(meta.lastSyncAt, locale) : t('rpaLichSu.dangDongBo'),
                    })}
              </span>
            )}
          </div>

          <Tile className="min-w-0">
            {ls.isPending ? (
              <Skeleton className="m-4 h-64" />
            ) : ls.isError ? (
              <LoiRpa loi={ls.error} onThuLai={() => void ls.refetch()} />
            ) : dong.length === 0 ? (
              dangLoc ? (
                <Empty icon={SearchOutlined} title={t('rpaLichSu.rongLoc')} />
              ) : (
                <Empty icon={HistoryOutlined} title={t('rpaLichSu.rong')} description={t('rpaLichSu.rongMoTa')} />
              )
            ) : (
              <DataTable<DongLichSu>
                caption={tieuDe}
                dataSource={dong}
                loading={ls.isFetching}
                pagination={{
                  current: meta?.page ?? truyVan.page,
                  pageSize: meta?.pageSize ?? truyVan.pageSize,
                  total: meta?.total ?? 0,
                  pageSizeOptions: CO_TRANG_LICH_SU.map(String),
                  showSizeChanger: true,
                  showTotal: (tong, [tu, den]) =>
                    t('rpaLichSu.tongTrang', { tu: soVN(tu, locale), den: soVN(den, locale), tong: soVN(tong, locale) }),
                  onChange: (page, pageSize) =>
                    setTruyVan((q) => ({ ...q, page: pageSize === q.pageSize ? page : 1, pageSize })),
                }}
                columns={[
                  {
                    title: t('chung.stt'),
                    key: 'stt',
                    width: 56,
                    render: (_, _d, i) => (
                      <span className="tabular-nums">
                        {soVN(((meta?.page ?? 1) - 1) * (meta?.pageSize ?? truyVan.pageSize) + i + 1, locale)}
                      </span>
                    ),
                  },
                  {
                    title: t('rpaLichSu.cotThoiGian'),
                    key: 'createdAt',
                    width: 150,
                    render: (_, d) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(d.createdAt, locale)}</span>,
                  },
                  {
                    title: t('rpaLichSu.cotLuot'),
                    key: 'name',
                    render: (_, d) => (
                      <div className="min-w-0 max-w-xs">
                        <p className="font-medium text-content-primary">{d.name || d.workflowId || KHONG_CO}</p>
                        <p className="break-all font-mono text-2xs text-content-muted">{d.id}</p>
                      </div>
                    ),
                  },
                  { title: t('rpaLichSu.cotTrangThai'), key: 'status', render: (_, d) => <BadgeTrangThaiLuot status={d.status} /> },
                  {
                    title: t('rpaLichSu.cotKetThuc'),
                    key: 'updatedAt',
                    width: 150,
                    render: (_, d) => (
                      <span className="whitespace-nowrap text-xs tabular-nums">
                        {daXong(d.status) ? ngayGioVN(d.updatedAt, locale) : KHONG_CO}
                      </span>
                    ),
                  },
                  {
                    title: t('rpaLichSu.cotKiemLoi'),
                    key: 'errorReport',
                    // Lượt đã xong luôn có nút, kể cả không lỗi ("Xem lỗi (0)"); lượt đang chạy chưa có gì để xem.
                    render: (_, d) => {
                      if (!daXong(d.status)) return <span className="text-content-muted">{KHONG_CO}</span>;
                      const so = d.errorReport?.eventCount ?? 0;
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          icon={so > 0 ? <WarningOutlined /> : <CheckCircleOutlined />}
                          onClick={() => setXemLoi(d)}
                        >
                          {t('rpaLichSu.xemLoi', { so: soVN(so, locale) })}
                        </Button>
                      );
                    },
                  },
                  {
                    title: t('rpaLichSu.cotLogRobot'),
                    key: 'robotLog',
                    // Luôn có nút: lượt Robot chưa gửi log vẫn mở được để thấy "thiếu log".
                    render: (_, d) => (
                      <Button
                        variant="outline"
                        size="sm"
                        icon={d.robotLog ? <FileTextOutlined /> : <WarningOutlined />}
                        onClick={() => setXemLog(d)}
                      >
                        {t('rpaLichSu.xemLog')}
                      </Button>
                    ),
                  },
                  {
                    title: t('rpaLichSu.cotDuLieu'),
                    key: 'duLieu',
                    render: (_, d) => (
                      <Button variant="outline" size="sm" icon={<DatabaseOutlined />} onClick={() => dieuHuong(duongDuLieu(d))}>
                        {t('rpaLichSu.xemDuLieu')}
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Tile>
        </>
      )}

      {xemLoi && <HopBaoCaoInstance dong={xemLoi} onClose={() => setXemLoi(null)} />}
      {xemLog && (
        <HopLogRobot
          instanceId={xemLog.instanceId}
          ten={xemLog.name || xemLog.workflowId || xemLog.id}
          onClose={() => setXemLog(null)}
        />
      )}
    </div>
  );
}
